import {
  Box,
  Button,
  Dialog,
  Icon,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { DEFAULT_DATE_FORMAT_FNS, FilterKeys, removeObjectEmptyArrayValues } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupSelectMultiple,
  GenderSelectMultiple,
  GroupSelectMultiple,
  NationalitySelectMultiple,
  PropertyTypeSelectMultiple,
  SchoolPeriodsRangeSelect,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
} from '@schooly/components/filters';
import { Genders, Nationalities, SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { CrossIcon, InformationIcon, ModalContent, ModalFooter, ModalHeader } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format, isAfter, isBefore, max, min } from 'date-fns';
import { FC, PropsWithChildren, ReactNode, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  AddStudentsFilters,
  AddStudentsFiltersKey,
  AddStudentsFiltersPopupProps,
} from '../../../../components/common/AddStudentsExternalSidebar/AddStudentsFiltersPopup';
import { useSchool } from '../../../../hooks/useSchool';
import useSchoolYears from '../../../../hooks/useSchoolYears';

type CommonFilterKey = Exclude<AddStudentsFiltersKey, FilterKeys.Gender | FilterKeys.Nationality>;

export const GroupStudentsFilterPopup: FC<AddStudentsFiltersPopupProps> = ({
  excludeGroups: initialExcludeGroups,
  filtersAvailable,
  onClose,
  onSetFilters,
  filters: initialFilters,
  defaultFilters,
}) => {
  const { formatMessage } = useIntl();
  const { defaultValidity } = useSchoolYears();
  const { schoolId } = useSchool();
  const { permissions } = useAuth();
  const isGroupViewer = permissions.includes('group_viewer');

  const [filters, setFilters] = useState<AddStudentsFilters>(initialFilters);
  const [excludeGroups, setExcludeGroups] = useState<boolean>(initialExcludeGroups ?? false);

  const handleApply = useCallback(() => {
    onSetFilters(filters, initialExcludeGroups !== undefined ? excludeGroups : undefined);
    onClose();
  }, [onSetFilters, filters, initialExcludeGroups, excludeGroups, onClose]);

  const handleClear = useCallback(() => {
    setFilters(defaultFilters);
  }, [defaultFilters]);

  const availableOptions = useMemo(
    () => removeObjectEmptyArrayValues(defaultFilters),
    [defaultFilters],
  );

  const isRequired = useCallback(
    (key: AddStudentsFiltersKey) => !!availableOptions[key]?.length,
    [availableOptions],
  );

  const availableDateRange = defaultFilters[FilterKeys.Date];

  const shouldDisableDay = useCallback(
    (date: Date) => {
      if (!availableDateRange?.[0] || !availableDateRange?.[1]) return false;
      const start = newDateTimezoneOffset(availableDateRange[0]);
      const end = newDateTimezoneOffset(availableDateRange[1]);
      return isBefore(date, start) || isAfter(date, end);
    },
    [availableDateRange],
  );

  const clampDateRange = useCallback(
    (from: Date, to: Date): [Date, Date] | null => {
      if (!availableDateRange?.[0] || !availableDateRange?.[1]) return [from, to];
      const start = newDateTimezoneOffset(availableDateRange[0]);
      const end = newDateTimezoneOffset(availableDateRange[1]);

      if (isAfter(from, end) || isBefore(to, start)) return null;

      return [max([from, start]), min([to, end])];
    },
    [availableDateRange],
  );

  const {
    onSetDate,
    onSelectStudentStatusId,
    onSelectAgeGroup,
    onSelectGroupId,
    onSelectHouseId,
    onClearStudentStatus,
    onClearAgeGroup,
    onClearGroup,
    onClearHouse,
    onSelectSubject,
    onSelectNationality,
    onSelectGender,
    onClearSubject,
    onClearNationality,
    onClearGender,
  } = useMemo(() => {
    const updateFilter = (key: CommonFilterKey) => (id: string) => {
      setFilters((v) => ({
        ...v,
        [key]:
          v[key] && v[key]?.includes(id)
            ? v[key]?.filter((ct) => ct !== id)
            : [...(v[key] || []), id],
      }));
    };
    const updateFilterMultipleValue = (key: CommonFilterKey) => (ids: string[]) => {
      setFilters((v) => {
        const next = toggleMultipleValueArrayProperty(v[key], ids);
        // A required field can never be emptied: keep the first toggled value selected.
        if (isRequired(key) && !next.length) return { ...v, [key]: ids.slice(0, 1) };
        return { ...v, [key]: next };
      });
    };
    const clearFilter = (key: AddStudentsFiltersKey) => () => {
      setFilters((v) => ({
        ...v,
        [key]: [],
      }));
    };

    const onSelectGender = (v: Genders) => {
      setFilters((criteria) => ({
        ...criteria,
        [FilterKeys.Gender]: criteria['gender']?.includes(v)
          ? criteria['gender']?.filter((ct) => ct !== v)
          : [...(criteria['gender'] || []), v],
      }));
    };

    const onSelectNationality = (v: Nationalities) => {
      setFilters((criteria) => ({
        ...criteria,
        [FilterKeys.Nationality]: criteria['nationality']?.includes(v)
          ? criteria['nationality']?.filter((ct) => ct !== v)
          : [...(criteria['nationality'] || []), v],
      }));
    };

    return {
      onSetDate: ([fromDate, toDate]: [Date, Date]) =>
        setFilters((s) => ({
          ...s,
          [FilterKeys.Date]: [
            format(fromDate, DEFAULT_DATE_FORMAT_FNS),
            format(toDate, DEFAULT_DATE_FORMAT_FNS),
          ],
        })),
      onSelectGender,
      onSelectNationality,
      onSelectSubject: updateFilter(FilterKeys.Subject),
      onSelectStudentStatusId: updateFilter(FilterKeys.Status),
      onSelectAgeGroup: updateFilterMultipleValue(FilterKeys.AgeGroup),
      onSelectHouseId: updateFilter(FilterKeys.House),
      onSelectGroupId: updateFilter(FilterKeys.Group),
      onClearStudentStatus: clearFilter(FilterKeys.Status),
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
      onClearHouse: clearFilter(FilterKeys.House),
      onClearGroup: clearFilter(FilterKeys.Group),
      onClearSubject: clearFilter(FilterKeys.Subject),
      onClearNationality: clearFilter(FilterKeys.Nationality),
      onClearGender: clearFilter(FilterKeys.Gender),
    };
  }, [isRequired]);

  const groupFilters = useMemo(() => {
    if (!filters.date) return undefined;

    return { [FilterKeys.IntersectDate]: filters.date };
  }, [filters.date]);

  if (!schoolId) return null;
  return (
    <Dialog
      fullWidth
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,

        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
      open
      onClose={onClose}
    >
      <ModalHeader active title={formatMessage({ id: 'reports-Filter-Title' })}>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        {filtersAvailable.includes(FilterKeys.Date) && (
          <FilterRow
            label={
              <Stack direction="row" alignItems="center" gap={0.5}>
                <Typography color="common.grey2">
                  {formatMessage({ id: 'schoolProperty-Date' })}
                </Typography>
                <Tooltip
                  title={formatMessage({ id: 'groups-StudentFilters-DateHint' })}
                  componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
                >
                  <Icon fontSize="small" sx={{ color: 'common.grey2' }}>
                    <InformationIcon />
                  </Icon>
                </Tooltip>
              </Stack>
            }
          >
            <SchoolPeriodsRangeSelect
              borderOnHover
              schoolId={schoolId}
              defaultSchoolYear={defaultValidity}
              hasManagePermission={permissions.includes('school_admin')}
              onSetDate={onSetDate}
              date={filters[FilterKeys.Date] || []}
              shouldDisableDay={shouldDisableDay}
              clampDateRange={clampDateRange}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Status) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-Status' })}>
            <PropertyTypeSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              onClear={isRequired(FilterKeys.Status) ? undefined : onClearStudentStatus}
              optionsTitle={formatMessage({ id: 'schoolProperty-Status-plural' })}
              userRole={SchoolUserRole.Student}
              propertyType={SchoolPropertyType.Status}
              selectedIds={filters.status || []}
              onSelectId={onSelectStudentStatusId}
              availableIds={availableOptions?.[FilterKeys.Status]}
              required={isRequired(FilterKeys.Status)}
              testId={DROPDOWN.STATUS}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.AgeGroup) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-AgeGroup' })}>
            <AgeGroupSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              onClear={isRequired(FilterKeys.AgeGroup) ? undefined : onClearAgeGroup}
              optionsTitle={formatMessage({ id: 'schoolProperty-AgeGroup-plural' })}
              userRole={SchoolUserRole.Student}
              selectedIds={filters.age_group || []}
              onSelect={onSelectAgeGroup}
              availableIds={availableOptions?.[FilterKeys.AgeGroup]}
              required={isRequired(FilterKeys.AgeGroup)}
              testId={DROPDOWN.AGE_GROUP}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.House) && (
          <FilterRow label={formatMessage({ id: 'schoolProperty-House' })}>
            <PropertyTypeSelectMultiple
              borderOnHover
              allowArchived
              schoolId={schoolId}
              onClear={isRequired(FilterKeys.House) ? undefined : onClearHouse}
              optionsTitle={formatMessage({ id: 'schoolProperty-House-plural' })}
              userRole={SchoolUserRole.Student}
              propertyType={SchoolPropertyType.House}
              selectedIds={filters.house || []}
              onSelectId={onSelectHouseId}
              availableIds={availableOptions?.[FilterKeys.House]}
              required={isRequired(FilterKeys.House)}
              testId={DROPDOWN.HOUSE}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Subject) && (
          <FilterRow label={formatMessage({ id: 'groups-GroupSubject' })}>
            <SubjectSelectMultiple
              borderOnHover
              schoolId={schoolId}
              onClear={isRequired(FilterKeys.Subject) ? undefined : onClearSubject}
              optionsTitle={formatMessage({ id: 'groups-GroupSubject-plural' })}
              selectedIds={filters.subject || []}
              onSelectSubjectId={onSelectSubject}
              availableIds={availableOptions?.[FilterKeys.Subject]}
              required={isRequired(FilterKeys.Subject)}
              testId={DROPDOWN.SUBJECT}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Gender) && (
          <FilterRow label={formatMessage({ id: 'peopleDetail-Gender' })}>
            <GenderSelectMultiple
              borderOnHover
              onClear={isRequired(FilterKeys.Gender) ? undefined : onClearGender}
              selectedGenders={filters.gender || []}
              onSelectGender={onSelectGender}
              availableValues={availableOptions?.[FilterKeys.Gender]}
              required={isRequired(FilterKeys.Gender)}
              testId={DROPDOWN.GENDER}
            />
          </FilterRow>
        )}
        {filtersAvailable.includes(FilterKeys.Nationality) && (
          <FilterRow label={formatMessage({ id: 'peopleDetail-Nationality' })}>
            <NationalitySelectMultiple
              borderOnHover
              onClear={isRequired(FilterKeys.Nationality) ? undefined : onClearNationality}
              selectedNationalities={filters.nationality || []}
              onSelectNationality={onSelectNationality}
              availableValues={availableOptions?.[FilterKeys.Nationality]}
              required={isRequired(FilterKeys.Nationality)}
              testId={DROPDOWN.NATIONALITY}
            />
          </FilterRow>
        )}
        {isGroupViewer && filtersAvailable.includes(FilterKeys.Group) && (
          <>
            <FilterRow
              label={`${
                excludeGroups ? `${formatMessage({ id: 'filter-NotIn' })} ` : ''
              }${formatMessage({ id: 'groups-Group' })}`}
            >
              <GroupSelectMultiple
                borderOnHover
                onClear={onClearGroup}
                schoolId={schoolId}
                filters={groupFilters}
                selectedIds={filters.group || []}
                onSelectGroupId={onSelectGroupId}
                testId={DROPDOWN.GROUP}
              />
            </FilterRow>
            {initialExcludeGroups !== undefined && (
              <FilterRow label="">
                <Stack flexDirection="row" alignItems="center" gap={1} pt={0.5} pl={2}>
                  <Typography color={!excludeGroups ? 'text.primary' : 'text.secondary'}>
                    {formatMessage({ id: 'filter-include' })}
                  </Typography>
                  <Switch
                    checked={excludeGroups}
                    onChange={() => setExcludeGroups(!excludeGroups)}
                    inputProps={{ 'aria-label': formatMessage({ id: 'filter-exclude' }) }}
                  />
                  <Typography color={excludeGroups ? 'text.primary' : 'text.secondary'}>
                    {formatMessage({ id: 'filter-exclude' })}
                  </Typography>
                </Stack>
              </FilterRow>
            )}
          </>
        )}
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={handleClear}>
          {formatMessage({ id: 'filter-ResetToDefault' })}
        </Button>
        <Button onClick={handleApply}>{formatMessage({ id: 'filter-Apply' })}</Button>
      </ModalFooter>
    </Dialog>
  );
};

const FilterRow: FC<PropsWithChildren<{ label: ReactNode }>> = ({ label, children }) => (
  <Stack flexDirection="row" alignItems="center" gap={2}>
    <Box sx={{ flex: '1 1 15%' }}>
      {typeof label === 'string' ? <Typography color="common.grey2">{label}</Typography> : label}
    </Box>
    <Box sx={{ flex: '1 1 85%' }}>{children}</Box>
  </Stack>
);
